import React from "react";
import robotoLight from "../assets/fonts/Roboto-Light.ttf";
import robotoRegular from "../assets/fonts/Roboto-Regular.ttf";
import robotoSemiBold from "../assets/fonts/Roboto-SemiBold.ttf";

// Vector PDF of a CDB form as laid out in the modal (public/forms/cdb/): every text line, entered value,
// rule and tick box is read from the rendered A4 sheets and redrawn at the same place with @react-pdf.
// Warning badges are screen-only and are left out, as when printing.

const PX_TO_PT = 0.75;                 // CSS px (96 dpi) → PDF pt (72 dpi)
const ROBOTO_ASCENT = 1900 / 2048;     // with lineHeight 1, react-pdf puts the baseline at top + ascent × size
const RULE_PT = 0.424;                 // rule weight of the SBA model forms
const FONTS = [
  { weight: 300, src: robotoLight, measure: "CdbPdfRobotoLight" },     // form wording
  { weight: 400, src: robotoRegular, measure: "CdbPdfRobotoRegular" }, // entered values (same font as the form)
  { weight: 600, src: robotoSemiBold, measure: "CdbPdfRobotoSemiBold" }, // bold passages
];

let fontsReady = null;
function loadFonts(Font) {
  if (!fontsReady) {
    Font.register({ family: "CdbPdfRoboto", fonts: FONTS.map(({ src, weight }) => ({ src, fontWeight: weight })) });
    // Same files in the browser, to measure widths and fit each line to the form's layout.
    fontsReady = Promise.all(FONTS.map(async ({ src, measure }) => {
      const face = new FontFace(measure, `url(${src})`);
      await face.load();
      document.fonts.add(face);
    }));
  }
  return fontsReady;
}

function toHex(color) {
  const m = String(color).match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3) return "#000000";
  return `#${m.slice(0, 3).map((v) => Math.round(Number(v)).toString(16).padStart(2, "0")).join("")}`;
}

// Roboto has no narrow no-break space; a no-break space keeps "25 %" together.
const pdfText = (text) => text.replace(/[ \t\n\r]+/g, " ").replace(/ /g, " ");

/** Positions (in px, relative to each sheet) of everything drawn on the form's sheets. */
export function snapshotCdbFormSheets(doc) {
  const view = doc.defaultView;
  const ctx = document.createElement("canvas").getContext("2d");
  const tickColor = toHex(view.getComputedStyle(doc.documentElement).getPropertyValue("--entry").trim() || "#3C5977");
  return [...doc.querySelectorAll(".sheet")].map((sheet) => {
    const base = sheet.getBoundingClientRect();
    const items = [];
    const visible = (el) => {
      for (let e = el; e && e !== sheet; e = e.parentElement) {
        const cs = view.getComputedStyle(e);
        if (cs.display === "none" || cs.visibility === "hidden") return false;
      }
      return true;
    };

    // Printed wording: one run per text node (lines are split by <br>), arrows drawn separately.
    const walker = doc.createTreeWalker(sheet, view.NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const parent = node.parentElement;
      if (!parent || !node.nodeValue.trim() || parent.closest(".field-hint") || !visible(parent)) continue;
      const cs = view.getComputedStyle(parent);
      const marker = doc.createElement("span");
      marker.style.cssText = "display:inline-block;width:0;height:0;vertical-align:baseline";
      node.after(marker);
      const baseline = marker.getBoundingClientRect().top - base.top;
      marker.remove();

      let offset = 0;
      for (const piece of node.nodeValue.split(/(→)/)) {
        const range = doc.createRange();
        if (piece === "→") {
          range.setStart(node, offset);
          range.setEnd(node, offset + 1);
          const r = range.getBoundingClientRect();
          items.push({ kind: "arrow", x: r.left - base.left, w: r.width, baseline, sizePx: parseFloat(cs.fontSize), color: toHex(cs.color) });
        } else {
          const lead = piece.search(/[^ \t\n\r]/);
          if (lead >= 0) {
            const end = offset + piece.replace(/[ \t\n\r]+$/, "").length;
            range.setStart(node, offset + lead);
            range.setEnd(node, end);
            const r = range.getBoundingClientRect();
            items.push({
              kind: "text", text: pdfText(node.nodeValue.slice(offset + lead, end)),
              x: r.left - base.left, width: r.width, baseline,
              sizePx: parseFloat(cs.fontSize), color: toHex(cs.color),
              // The form sets bold through its bold face ("CDB Bold"), not always through font-weight.
              weight: Number(cs.fontWeight) >= 600 || /CDB Bold/.test(cs.fontFamily) ? 600 : 300,
            });
          }
        }
        offset += piece.length;
      }
    }

    // Fields: rules, entered values and tick boxes.
    for (const input of sheet.querySelectorAll("input")) {
      if (!visible(input)) continue;
      const r = input.getBoundingClientRect();
      const cs = view.getComputedStyle(input);
      const x = r.left - base.left;
      const y = r.top - base.top;
      if (input.type === "checkbox" || input.type === "radio") {
        items.push({ kind: "box", x, y, size: r.width, checked: input.checked, color: tickColor });
        continue;
      }
      if (parseFloat(cs.borderBottomWidth) > 0) items.push({ kind: "rule", x, y: y + r.height, w: r.width });
      const value = input.value.trim();
      // An address holding only a country is left blank, to be completed by hand (as when printing).
      if (!value || input.hasAttribute("data-print-blank")) continue;
      ctx.font = cs.font;
      const m = ctx.measureText(value);
      const contentTop = y + parseFloat(cs.borderTopWidth) + parseFloat(cs.paddingTop);
      const contentH = r.height - parseFloat(cs.borderTopWidth) - parseFloat(cs.borderBottomWidth)
        - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const asc = m.fontBoundingBoxAscent;
      const desc = m.fontBoundingBoxDescent;
      items.push({
        kind: "text", entry: true, text: pdfText(value),
        x: x + parseFloat(cs.borderLeftWidth) + parseFloat(cs.paddingLeft),
        width: m.width, maxWidth: r.width - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight),
        baseline: contentTop + (contentH - (asc + desc)) / 2 + asc,
        sizePx: parseFloat(cs.fontSize), weight: 400, color: toHex(cs.color),
      });
    }
    return items;
  });
}

/** PDF (Blob) of one or several forms (the modal's iframe documents), their pages in order. */
export async function buildCdbFormPdf(docs, title = "CDB form") {
  const { pdf, Document, Page, Text, View, Svg, Line, Polyline, Font } = await import("@react-pdf/renderer");
  await loadFonts(Font);
  const sheets = [].concat(docs).flatMap(snapshotCdbFormSheets);
  const ctx = document.createElement("canvas").getContext("2d");
  const pt = (px) => px * PX_TO_PT;

  const renderItem = (item, i) => {
    if (item.kind === "rule") {
      return <View key={i} style={{ position: "absolute", left: pt(item.x), top: pt(item.y) - RULE_PT, width: pt(item.w), height: RULE_PT, backgroundColor: "#000000" }} />;
    }
    if (item.kind === "box") {
      const s = pt(item.size);
      return (
        <View key={i} style={{ position: "absolute", left: pt(item.x), top: pt(item.y), width: s, height: s, borderWidth: 0.5, borderColor: "#000000", borderStyle: "solid" }}>
          {item.checked ? (
            <Svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} style={{ position: "absolute", left: -0.5, top: -0.5 }}>
              <Line x1={0.8} y1={0.8} x2={s - 0.8} y2={s - 0.8} stroke={item.color} strokeWidth={1} />
              <Line x1={s - 0.8} y1={0.8} x2={0.8} y2={s - 0.8} stroke={item.color} strokeWidth={1} />
            </Svg>
          ) : null}
        </View>
      );
    }
    if (item.kind === "arrow") {
      const w = pt(item.w);
      const size = pt(item.sizePx);
      const mid = size * 0.35;           // arrow on the text's math axis
      return (
        <Svg key={i} width={w} height={size} viewBox={`0 0 ${w} ${size}`}
          style={{ position: "absolute", left: pt(item.x), top: pt(item.baseline) - size + (size - mid * 2) / 2 }}>
          <Line x1={w * 0.08} y1={size - mid} x2={w * 0.9} y2={size - mid} stroke={item.color} strokeWidth={0.6} />
          <Polyline points={`${w * 0.62},${size - mid - size * 0.2} ${w * 0.9},${size - mid} ${w * 0.62},${size - mid + size * 0.2}`}
            stroke={item.color} strokeWidth={0.6} fill="none" />
        </Svg>
      );
    }
    // Wording: fit Roboto to the width the line has on the form, so the layout is kept.
    // Entered values are already Roboto (heavier on screen only): true size, shrunk only if too long for the field.
    const font = FONTS.find((f) => f.weight === item.weight);
    ctx.font = `${item.sizePx}px "${font.measure}"`;
    const roboto = ctx.measureText(item.text).width || 1;
    const fit = item.entry
      ? Math.max(0.6, Math.min(1, item.maxWidth / roboto))
      : Math.min(1.15, Math.max(0.85, item.width / roboto));
    const size = pt(item.sizePx) * fit;
    return (
      <Text key={i} style={{
        position: "absolute", left: pt(item.x), top: pt(item.baseline) - ROBOTO_ASCENT * size,
        width: 595.28 - pt(item.x), fontFamily: "CdbPdfRoboto", fontWeight: item.weight, fontSize: size,
        lineHeight: 1, color: item.color,
      }}>{item.text}</Text>
    );
  };

  return pdf(
    <Document title={title} creator="myKyc" producer="myKyc">
      {sheets.map((items, p) => (
        <Page key={p} size="A4" style={{ position: "relative", backgroundColor: "#ffffff" }}>
          {items.map(renderItem)}
        </Page>
      ))}
    </Document>
  ).toBlob();
}

/** Builds the PDF of the forms shown in `docs` and downloads it as `fileName`. */
export async function downloadCdbFormPdf(docs, fileName) {
  const blob = await buildCdbFormPdf(docs, fileName.replace(/\.pdf$/i, ""));
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
